import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store } from '@warp-drive/core';
import { reloadRecord } from './save-record.ts';
import { camelizeAttributes } from '../utils/camelize-attributes.ts';
import type { EligibilityFileJobLog } from '../data/resources/eligibility-file-job-log.type.ts';
import {
  POLLING_STATUSES,
  TERMINAL_STATUSES,
} from '../data/resources/eligibility-file-job-log.constants.ts';

/**
 * EligibilityFileJobLog action request builders.
 *
 * Replaces the duplicated custom methods on the near-identical
 * `tio-employee/app/adapters/eligibility-file-job-log.ts` and
 * `tio-admin/app/adapters/eligibility-file-job-log.ts`. Both adapter copies
 * built every URL via `this.buildURL('eligibility-files') + '/<action>'` — i.e.
 * the resource path is the PLURAL `eligibility-files`, NOT the model's
 * `eligibility-file-job-log` dasherized type — so the builders resolve the base
 * URL from `eligibility-files` to stay byte-identical.
 *
 * Neither adapter consulted `demoMode`, so there is NO demo seam to push to the
 * call site here, and none of these methods carries a
 * `by:{name,userId,actingWithRole}` actor — the bodies are flat `{ legacy_id,
 * company_id, process_id }` (or, for the upload-url request, `{ 'file-name',
 * legacy_id, company_id, strategy }`).
 *
 * BODY CASING — preserved EXACTLY from the legacy adapters (snake/kebab mixed):
 * the process/abort/schedule/rerun bodies are snake_case (`legacy_id`,
 * `company_id`, `process_id`); the get-upload-url body adds a KEBAB-case
 * `'file-name'` key alongside the same snake_case `legacy_id` / `company_id`
 * plus `strategy`. The legacy adapters sent these via
 * `this.ajax(url, 'POST', { data: JSON.stringify({...}) })` — Ember Data's
 * `data` option IS the request body, and a pre-stringified `data` is sent
 * verbatim, so the wire body is the inner object exactly as stringified here.
 *
 * `getUploadUrlRequest` returns a JSON:API `{ data: {...} }` document; the
 * legacy `getUploadRecord` did `this.store.pushPayload(response)` then
 * `peekRecord('eligibility-file-job-log', response.data.id)`. Routing through
 * `store.request(...)` materialises the response into the cache natively, so the
 * call site reads the returned record off `content.data` instead.
 *
 * TODO(wave 3 — admin): the admin-only adapter actions
 * `fetchRecords` (GET /eligibility-files/:id/records/:category?page&per_page),
 * `fetchMetaDiff` (GET /eligibility-files/:id/meta-diff), and
 * `exportRecords` (GET /eligibility-files/:id/records/:category/export, a
 * `text/csv` blob download) are single-app (admin) surface with no dedup win
 * today. They convert when the admin call sites convert in wave 3; do NOT add
 * them here pre-emptively.
 */

const eligibilityFilesUrlOptions: QueryUrlOptions = {
  identifier: { type: 'eligibility-files' },
  op: 'query',
  resourcePath: 'eligibility-files',
};

function eligibilityFilesUrl(path?: string): string {
  const base = buildBaseURL(eligibilityFilesUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/** The snake_case body shared by process / abort / schedule / rerun. */
export interface EligibilityFileJobBody {
  legacy_id: string;
  company_id: string;
  process_id: string;
}

/**
 * POST /eligibility-files/process-eligibility-file
 * body `{ legacy_id, company_id, process_id }`
 *
 * Mirrors `EligibilityFileJobLogAdapter#processJob`, which built the URL via
 * `this.buildURL('eligibility-files') + '/process-eligibility-file'` and sent
 * `this.ajax(url, 'POST', { data: JSON.stringify({ legacy_id, company_id, process_id }) })`.
 */
export function processEligibilityFileRequest(
  legacy_id: string,
  company_id: string,
  process_id: string
): RequestInfo {
  return {
    url: eligibilityFilesUrl('process-eligibility-file'),
    method: 'POST',
    body: JSON.stringify({ legacy_id, company_id, process_id }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /eligibility-files/abort-eligibility-file
 * body `{ legacy_id, company_id, process_id }`
 *
 * Mirrors `EligibilityFileJobLogAdapter#abortJob`, which built the URL via
 * `this.buildURL('eligibility-files') + '/abort-eligibility-file'` and sent
 * `this.ajax(url, 'POST', { data: JSON.stringify({ legacy_id, company_id, process_id }) })`.
 */
export function abortEligibilityFileRequest(
  legacy_id: string,
  company_id: string,
  process_id: string
): RequestInfo {
  return {
    url: eligibilityFilesUrl('abort-eligibility-file'),
    method: 'POST',
    body: JSON.stringify({ legacy_id, company_id, process_id }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /eligibility-files/schedule-eligibility-file
 * body `{ legacy_id, company_id, process_id }`
 *
 * Mirrors `EligibilityFileJobLogAdapter#scheduleEligibilityFile` (admin-only
 * adapter method), which built the URL via
 * `this.buildURL('eligibility-files') + '/schedule-eligibility-file'` and sent
 * `this.ajax(url, 'POST', { data: JSON.stringify({ legacy_id, company_id, process_id }) })`.
 */
export function scheduleEligibilityFileRequest(
  legacy_id: string,
  company_id: string,
  process_id: string
): RequestInfo {
  return {
    url: eligibilityFilesUrl('schedule-eligibility-file'),
    method: 'POST',
    body: JSON.stringify({ legacy_id, company_id, process_id }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /eligibility-files/rerun-preprocessor
 * body `{ legacy_id, company_id, process_id }`
 *
 * Mirrors `EligibilityFileJobLogAdapter#reprocessJob` (admin-only adapter
 * method), which built the URL via
 * `this.buildURL('eligibility-files') + '/rerun-preprocessor'` and sent
 * `this.ajax(url, 'POST', { data: JSON.stringify({ legacy_id, company_id, process_id }) })`.
 */
export function rerunPreprocessorRequest(
  legacy_id: string,
  company_id: string,
  process_id: string
): RequestInfo {
  return {
    url: eligibilityFilesUrl('rerun-preprocessor'),
    method: 'POST',
    body: JSON.stringify({ legacy_id, company_id, process_id }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /eligibility-files/get-upload-url
 * body `{ 'file-name', legacy_id, company_id, strategy }`
 *
 * Mirrors `EligibilityFileJobLogAdapter#getUploadRecord`, which built the URL
 * via `this.buildURL('eligibility-files') + '/get-upload-url'` and sent
 * `this.ajax(url, 'POST', { data: JSON.stringify({ 'file-name': name, legacy_id, company_id, strategy }) })`
 * — note the KEBAB-case `'file-name'` key beside the snake_case `legacy_id` /
 * `company_id`, preserved EXACTLY here. The legacy method then
 * `pushPayload`-ed the `{ data: {...} }` response and `peekRecord`-ed the
 * resulting `eligibility-file-job-log` (a reified record with flat,
 * camelCased fields).
 *
 * Because this request runs with `SkipCache`, the response does NOT flow
 * through the cache and is NOT reified into a record — `content.data` is the
 * raw JSON:API resource `{ id, attributes: {...} }`. The call site must parse
 * it via `parseUploadUrlResponse` (same convention as `match-plan` /
 * `match-period`) rather than reading flat record fields off `content.data`.
 */
export function getUploadUrlRequest(
  name: string,
  legacy_id: string,
  company_id: string,
  strategy: string
): RequestInfo {
  return {
    url: eligibilityFilesUrl('get-upload-url'),
    method: 'POST',
    body: JSON.stringify({ 'file-name': name, legacy_id, company_id, strategy }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * The newly-created job-log fields the upload UI consumes from the
 * `getUploadUrlRequest` response. Only these three are read by the selector +
 * upload modal: `uploadUrl` (the signed URL the file is `PUT` to), `id` (for
 * the post-upload transition to the show route), and `processId` (for abort).
 */
export interface UploadJobLog {
  id: string;
  uploadUrl: string;
  processId: string;
}

interface UploadUrlResponseResource {
  id?: unknown;
  attributes?: Record<string, unknown>;
}

/** JSON:API attribute values are `unknown`; coerce non-strings (or missing) to ''. */
function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Parse the `getUploadUrlRequest` response into the flat fields the upload UI
 * needs. The request is `SkipCache`, so `content.data` is the raw JSON:API
 * resource (never reified into a record); read `id` + `attributes` directly,
 * camelizing attribute keys via the shared `camelizeAttributes` util so the
 * parse is robust regardless of the negotiated wire key format. Values are
 * validated as strings (these build a PUT URL + an abort request), falling back
 * to '' so a malformed response degrades safely rather than throwing.
 */
export function parseUploadUrlResponse(content: unknown): UploadJobLog {
  const resource = (content as { data?: UploadUrlResponseResource } | null)?.data ?? {};
  const attributes = camelizeAttributes(resource.attributes);
  return {
    id: asString(resource.id),
    uploadUrl: asString(attributes['uploadUrl']),
    processId: asString(attributes['processId']),
  };
}

/**
 * Interval handles per job-log record. Replaces the `#interval` instance field
 * on the legacy `EligibilityFileJobLogExtension` — WarpDrive resource
 * extensions can't hold instance state (only prototype members are exposed
 * via `features.prototype`), so the state lives here keyed by the record.
 */
const pollingIntervals = new WeakMap<EligibilityFileJobLog, ReturnType<typeof setInterval>>();

/**
 * Poll for status updates on the job log every 3s, reloading the record while
 * its status is in a "still working" state and clearing the timer once the
 * status is terminal. Replaces `jobLog.startPollingForStatus()` on the
 * extension. Restarts the timer if this record is already being polled.
 *
 * `onTick` rides the same timer, for job state the record reload does not
 * cover — the admin page's server-paged log entries and their filter facets.
 * It runs once more on the tick that observes the terminal status, flagged as
 * final, so entries written between the last reload and completion still land
 * and the caller knows no further tick can follow. A job that was already
 * terminal when polling started never runs it at all.
 */
export function startPollingForEligibilityStatus(
  store: Store,
  jobLog: EligibilityFileJobLog,
  onTick?: (isFinal: boolean) => void
) {
  stopPollingForEligibilityStatus(jobLog);
  // Seeded from the status at call time: a job that finishes inside the first
  // 3s still earns its final tick, which a flag set only by a tick would miss.
  let wasExecuting = (POLLING_STATUSES as readonly string[]).includes(jobLog.status);
  const interval = setInterval(() => {
    if ((POLLING_STATUSES as readonly string[]).includes(jobLog.status)) {
      wasExecuting = true;
      void reloadRecord(store, jobLog);
      onTick?.(false);
    } else if ((TERMINAL_STATUSES as readonly string[]).includes(jobLog.status)) {
      stopPollingForEligibilityStatus(jobLog);
      if (wasExecuting) onTick?.(true);
    }
  }, 3000);
  pollingIntervals.set(jobLog, interval);
}

export function stopPollingForEligibilityStatus(jobLog: EligibilityFileJobLog) {
  const interval = pollingIntervals.get(jobLog);
  if (interval) {
    clearInterval(interval);
    pollingIntervals.delete(jobLog);
  }
}
